<script setup>
// How everything in Tap Leagues fits together: a few group cards first, each
// group's areas inside it, each area's parts inside that; click a part to read
// about it. The same component serves the app's web view at /embed/system-map.
import SystemMap from '~/components/systemMap/SystemMap.vue';
import {SYSTEM_MAP_GROUPS, SYSTEM_MAP_NODES, SYSTEM_MAP_SECTIONS} from '~/utils/systemMap';

definePageMeta({
  name: 'SystemMap',
  path: '/help/system-map',
  // Nuxt's default scrollBehavior resolves {el: '#hash'} straight away on a
  // fresh load (from === START_LOCATION), before this page has mounted, so the
  // router warned it could not find the hash's element. A fresh load starts at
  // the top anyway: no scroll then. In-session hash changes (same path) still
  // scroll to the map's anchor element.
  scrollToTop: (to, from) => from.matched.length > 0,
});

const {setMeta} = useHeadService();
setMeta(
    'How it all fits together | Tap Leagues',
    `A map of every part of Tap Leagues and how they connect: clubs, players, memberships and passes, box leagues, ladders, tournaments, americano leagues, meets, matches, payments, bookings, chat, widgets and Clubhouse TV. ${SYSTEM_MAP_NODES.length} parts in ${SYSTEM_MAP_SECTIONS.length} areas, in ${SYSTEM_MAP_GROUPS.length} groups.`,
);

// The map fills what is left of the first screen exactly: measured from where
// it sits to the bottom of the window, less the keyboard line under it. The
// height goes straight onto the wrapper element (no binding), so the inline
// script below can set it before the first paint and the page's own measure
// can set it again without a hydration mismatch. The header and the title
// settle after the first paint (fonts, hydration), so the measure is repeated
// when they do: next tick, fonts ready, a ResizeObserver on the head and the
// body, and on resize. A short window (a phone on its side) hides the title
// (CSS) and takes a lower minimum, so the map's bar is always on screen (R48 L2).
// Since X128 the server renders v-main with the app bar's 64px already
// (useLayoutBoot); before that it rendered --v-layout-top: 0px and the 64px
// arrived on hydration. The inline measure still adds the bar's height if that
// padding is ever 0, so the first paint's height is the settled one either way
// and the map does not shrink and snap (R51 L3).
const wrapEl = ref(null);
const headEl = ref(null);
const footEl = ref(null);
const MIN_TALL = 420, MIN_SHORT = 200;
// Under the keyboard line on a desktop (was 16: the 8px back let an area at 1440×900 end in a gap at 11px, R56 M1)
const BOTTOM_GAP = 8;
/**
 * The app bar's height while v-main's top padding has not arrived yet (it lands a moment after hydration), else 0: the
 * same allowance the inline measure makes. Without it the page's own first measure (on mount, still before the
 * padding) made the map 64px too tall for a frame, the map fitted to that, then shrank and eased back (R56 M3).
 */
const pendingBarTop = () => {
  const main = document.querySelector('.v-main');
  const bar = document.querySelector('.v-app-bar');
  if (!main || !bar) return 0;
  return parseFloat(getComputedStyle(main).getPropertyValue('--v-layout-top')) > 0 ? 0 : bar.getBoundingClientRect().height;
};
const sizeMap = () => {
  const el = wrapEl.value;
  if (!el || typeof window === 'undefined') return;
  const top = el.getBoundingClientRect().top + window.scrollY + pendingBarTop();
  const foot = footEl.value && getComputedStyle(footEl.value).display !== 'none' ? footEl.value.getBoundingClientRect().height + 10 : 0;
  const pad = window.innerWidth < 960 ? 0 : BOTTOM_GAP;
  const min = window.innerHeight < 560 ? MIN_SHORT : MIN_TALL;
  const next = `${Math.max(min, Math.round(window.innerHeight - top - foot - pad))}px`;
  if (next !== el.style.height) el.style.height = next;
  el.setAttribute('data-sized', '1');
};
// The same measure, inline right after the keyboard line (the last thing it measures), so it runs as the parser reaches
// it, before the first paint: not at the end of the body, where the canvas had been painted once at the CSS guess of a
// height and the server's scale (R59 L1). Until it has run the map's box is not drawn. It then runs the map's own
// pre-paint fit (__smFit, see SystemMap.vue), which waits for it on this page (data-sm-sizer).
const sizerHtml = `<script>(function(){var el=document.querySelector('.smp-map');if(!el)return;var top=el.getBoundingClientRect().top+window.scrollY;var m=document.querySelector('.v-main');var bar=document.querySelector('.v-app-bar');if(m&&bar&&!(parseFloat(getComputedStyle(m).getPropertyValue('--v-layout-top'))>0))top+=bar.getBoundingClientRect().height;var f=document.querySelector('.smp-foot');var foot=f&&getComputedStyle(f).display!=='none'?f.getBoundingClientRect().height+10:0;var pad=window.innerWidth<960?0:${BOTTOM_GAP};var min=window.innerHeight<560?${MIN_SHORT}:${MIN_TALL};el.style.height=Math.max(min,Math.round(window.innerHeight-top-foot-pad))+'px';el.setAttribute('data-sized','1');if(window.__smFit)window.__smFit();})();<\/script>`;
let frame = 0;
let ro = null;
const timers = [];
const schedule = () => {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(sizeMap);
};
onMounted(() => {
  sizeMap();
  nextTick(schedule);
  try { document.fonts?.ready?.then(schedule); } catch (err) { /* no font API */ }
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(schedule);
    ro.observe(document.body);
    if (headEl.value) ro.observe(headEl.value);
  }
  for (const ms of [120, 400, 1000]) timers.push(setTimeout(schedule, ms));
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', schedule);
  window.removeEventListener('orientationchange', schedule);
  ro?.disconnect();
  cancelAnimationFrame(frame);
  timers.forEach(clearTimeout);
});
</script>

<template>
  <base-page>
    <v-container class="large smp">
      <div ref="headEl" class="smp-head">
        <div class="smp-head-text">
          <span class="smp-eyebrow"><NuxtLink to="/help">Help</NuxtLink> · System map</span>
          <h1 class="smp-title">How it all fits together</h1>
          <p class="smp-sub">
            Start with a group. Open it to see its areas, open an area to see its parts and how they hold together,
            and open a part to read what it is, who sees it and who manages it, and jump to what it connects to.
          </p>
        </div>
      </div>
      <div ref="wrapEl" class="smp-map" data-sm-sizer><system-map height="100%"/></div>
      <p ref="footEl" class="smp-foot">
        Keyboard: <kbd>/</kbd> search, <kbd>+</kbd> <kbd>−</kbd> zoom, <kbd>0</kbd> fit, <kbd>Esc</kbd> one level out, arrows pan. Share a part with its link, an area with
        <code>#payments</code> or a group with <code>#competition-types</code>.
      </p>
      <!-- The pre-paint measure (sizerHtml): run right here on a fresh load, inert after -->
      <div class="smp-sizer" aria-hidden="true" v-html="sizerHtml"/>
    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
.smp {
  max-width: 1600px;
  padding-top: 14px;
  padding-bottom: 24px;
}

.smp-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.smp-head-text {
  max-width: 720px;
}

/* The map's box: sized by the page to the window; this is only the first guess, never drawn (R59 L1) */
.smp-map {
  height: calc(100dvh - 190px);
  min-height: 260px;

  &:not([data-sized]) {
    visibility: hidden;
  }
}

.smp-sizer {
  display: none;
}

.smp-eyebrow {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));

  a {
    color: rgb(var(--v-theme-primary));
    text-decoration: none;
  }
}

.smp-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
  margin: 4px 0 6px;
  text-wrap: balance;
}

.smp-sub {
  font-size: 14px;
  line-height: 1.55;
  color: rgb(var(--v-theme-charcoal));
  margin: 0;
}

.smp-foot {
  margin: 10px 0 0;
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));

  kbd {
    display: inline-block;
    min-width: 18px;
    padding: 1px 5px;
    border: 1px solid rgb(var(--v-theme-border));
    border-bottom-width: 2px;
    border-radius: 5px;
    background: rgb(var(--v-theme-surface));
    font-family: inherit;
    font-size: 11px;
    text-align: center;
  }

  code {
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11.5px;
  }
}

@media (max-width: 959px) {
  .smp {
    padding-left: 0;
    padding-right: 0;
    padding-top: 8px;
  }

  .smp-head {
    padding: 0 16px;
  }

  .smp-title {
    font-size: 22px;
  }

  .smp-sub {
    display: none;
  }

  .smp-foot {
    display: none;
  }

  :deep(.sm) {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
}

/* A phone on its side: the title goes, the map takes the height */
@media (max-width: 959px) and (max-height: 560px) {
  .smp {
    padding-top: 0;
  }

  .smp-head {
    display: none;
  }

  .smp-map {
    min-height: 200px;
  }
}
</style>
